<script setup>
import { computed, onMounted, ref, defineEmits } from "vue"

import Lottie from "@/components/widgets/lottie.vue"
import animationData from "@/components/widgets/msoeawqm.json"
import * as persianTools from "@persian-tools/persian-tools"
import PersianDate from "persian-date"

import store from "../../state/store"

const users = computed(() => store.state.users)

import useUsers from "../../use/useUsers"
import useDebouncedFunction from "../../use/useDebouncedFunction"

import TableHeaderCell from "@/components/table/TableHeaderCell.vue"

const {
  userStatus,
  search,
  createdAtDate,
  perPage,
  sortField,
  sortDirection,
  getUsers,
  sortUsers,
} = useUsers()
const { handleInput } = useDebouncedFunction(getUsers)

const emit = defineEmits(["clickEdit", "clickWallet"])

onMounted(() => {
  getUsers()
})

const toPersianDateTime = (date) => {
  const [datePart, timePart] = date.split(" ")
  const [year, month, day] = datePart.split("-")
  const [hour, minute, second] = timePart.split(":")
  const gregorianDate = new Date(year, month - 1, day, hour, minute, second)
  const persianDate = new PersianDate(gregorianDate)
  return persianDate.format("YYYY/MM/DD HH:mm:ss")
}

function getForPage(ev, link) {
  ev.preventDefault()
  if (!link.url || link.active) {
    return
  }

  getUsers(link.url)
}

const defaultOptions = ref({
  animationData: animationData,
})

const editUser = (usr) => {
  emit("clickEdit", usr)
}

const walletUser = (usrId) => {
  emit("clickWallet", usrId)
}

const isAdmin = computed(() => {
  return store.state.user.data.ur === "admin" && store.getters.isAdmin
    ? true
    : false
})
</script>

<template>
  <b-card-body class="border-bottom-dashed border-bottom">
    <b-row class="g-3">
      <b-col xl="5">
        <div class="search-box">
          <input
            v-model="search"
            @input="handleInput"
            type="text"
            class="form-control search"
            placeholder="جستجوی کاربر، نام، نام خانوادگی، ایمیل و..."
          />
          <i class="ri-search-line search-icon"></i>
        </div>
      </b-col>

      <b-col xl="7">
        <b-row class="g-3">
          <b-col sm="4">
            <select
              @change="getUsers(null)"
              v-model="userStatus"
              class="form-select mb-3"
              aria-label="Default select example"
            >
              <option value="" selected>انتخاب وضعیت کاربر</option>
              <option value="0">غیرفعال</option>
              <option value="1">فعال</option>
            </select>
          </b-col>

          <b-col sm="4">
            <custom-date-picker
              v-model="createdAtDate"
              @change="getUsers(null)"
            ></custom-date-picker>
          </b-col>

          <b-col sm="4">
            <select
              @change="getUsers(null)"
              v-model="perPage"
              class="form-select mb-3"
              aria-label="Default select example"
            >
              <option value="" selected>نمایش تعداد ردیف</option>
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="30">30</option>
              <option value="50">50</option>
            </select>
          </b-col>
        </b-row>
      </b-col>
    </b-row>
  </b-card-body>
  <b-card-body>
    <div>
      <div class="table-responsive table-card mb-1">
        <table class="table align-middle" id="userTable">
          <thead class="table-light text-muted">
            <tr>
              <TableHeaderCell
                field="first_name"
                :sort-field="sortField"
                :sort-direction="sortDirection"
                @click="sortUsers('first_name')"
              >
                نام
              </TableHeaderCell>
              <TableHeaderCell
                field="last_name"
                :sort-field="sortField"
                :sort-direction="sortDirection"
                @click="sortUsers('last_name')"
              >
                نام خانوادگی
              </TableHeaderCell>
              <TableHeaderCell
                field="email"
                :sort-field="sortField"
                :sort-direction="sortDirection"
                @click="sortUsers('email')"
              >
                ایمیل
              </TableHeaderCell>
              <TableHeaderCell
                field="phone_number"
                :sort-field="sortField"
                :sort-direction="sortDirection"
                @click="sortUsers('phone_number')"
              >
                تلفن
              </TableHeaderCell>
              <TableHeaderCell
                field="created_at"
                :sort-field="sortField"
                :sort-direction="sortDirection"
                @click="sortUsers('created_at')"
              >
                تاریخ ثبت
              </TableHeaderCell>
              <TableHeaderCell
                field="updated_at"
                :sort-field="sortField"
                :sort-direction="sortDirection"
                @click="sortUsers('updated_at')"
              >
                آخرین ویرایش
              </TableHeaderCell>
              <TableHeaderCell
                field="referral_user"
                :sort-field="sortField"
                :sort-direction="sortDirection"
                @click="sortUsers('referral_user')"
              >
                معرف
              </TableHeaderCell>
              <TableHeaderCell
                field="status"
                :sort-field="sortField"
                :sort-direction="sortDirection"
                @click="sortUsers('status')"
              >
                وضعیت
              </TableHeaderCell>
              <TableHeaderCell
                field="total_flowers"
                :sort-field="sortField"
                :sort-direction="sortDirection"
                @click="sortUsers('total_flowers')"
              >
                شاخه گل
              </TableHeaderCell>
              <th class="sort" data-sort="action">عملیات</th>
            </tr>
          </thead>
          <tbody class="list" v-if="users.loading || !users.data.length">
            <tr>
              <td colspan="10">
                <div class="text-center">
                  <b-spinner
                    v-if="users.loading"
                    variant="dark"
                    style="width: 3rem; height: 3rem"
                  ></b-spinner>
                  <div v-else>
                    <lottie
                      class="avatar-xl"
                      colors="primary:#121331,secondary:#08a88a"
                      :options="defaultOptions"
                      :height="75"
                      :width="75"
                    />
                    <h5 class="mt-2">هیچ کاربری وجود ندارد!</h5>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>

          <tbody class="list" v-else>
            <tr v-for="(user, index) of users.data" :key="index">
              <td class="user_name">{{ user.name }}</td>
              <td class="user_surname">{{ user.surname }}</td>
              <td class="email">{{ user.email }}</td>
              <td class="phone">{{ persianTools.digitsEnToFa(user.phone) }}</td>
              <td class="date">{{ toPersianDateTime(user.created_at) }}</td>
              <td class="updated_at">
                {{ toPersianDateTime(user.updated_at) }}
              </td>
              <td class="referral_user">{{ user.referral }}</td>
              <td class="status">
                <span
                  class="badge text-uppercase"
                  :class="{
                    'badge-soft-success': user.status == 1,
                    'badge-soft-danger': user.status == 0,
                  }"
                  >{{ user.status ? "فعال" : "غیرفعال" }}</span
                >
              </td>
              <td>
                <b-link class="text-success d-inline-block wallet-item-btn">
                  <i class="ri-leaf-fill fs-16"></i> {{ user.totalFlowers }}
                </b-link>
              </td>
              <td>
                <ul class="list-inline hstack gap-2 mb-0">
                  <li
                    v-if="isAdmin"
                    class="list-inline-item"
                    data-bs-toggle="tooltip"
                    data-bs-trigger="hover"
                    data-bs-placement="top"
                    title="کیف پول کاربر"
                  >
                    <b-link
                      class="text-info d-inline-block wallet-item-btn"
                      @click="walletUser(user.id)"
                    >
                      <i class="ri-wallet-3-fill fs-16"></i>
                    </b-link>
                  </li>
                  <li
                    class="list-inline-item"
                    data-bs-toggle="tooltip"
                    data-bs-trigger="hover"
                    data-bs-placement="top"
                    title="صفحه گفتگو با کاربر"
                  >
                    <RouterLink
                      :to="{
                        name: 'users.messages',
                        params: { userId: user.id },
                      }"
                    >
                      <i class="ri-chat-3-fill fs-16"></i>
                    </RouterLink>
                  </li>
                  <li
                    class="list-inline-item edit"
                    data-bs-toggle="tooltip"
                    data-bs-trigger="hover"
                    data-bs-placement="top"
                    title="ویرایش کاربر"
                  >
                    <b-link
                      variant="text-primary"
                      class="d-inline-block edit-item-btn"
                      @click="editUser(user)"
                    >
                      <i class="ri-pencil-fill fs-16"></i>
                    </b-link>
                  </li>
                  <li
                    class="list-inline-item"
                    data-bs-toggle="tooltip"
                    data-bs-trigger="hover"
                    data-bs-placement="top"
                    title="حذف کاربر"
                  >
                    <b-link
                      class="text-danger d-inline-block remove-item-btn"
                      @click="deletedata(item)"
                    >
                      <i class="ri-delete-bin-5-fill fs-16"></i>
                    </b-link>
                  </li>
                  <li
                    class="list-inline-item"
                    data-bs-toggle="tooltip"
                    data-bs-trigger="hover"
                    data-bs-placement="top"
                    title="تیکت های پشتیبانی"
                    v-if="user.unreadMessagesCount > 0"
                  >
                    <RouterLink
                      :to="{
                        name: 'users.messages',
                        params: { userId: user.id },
                      }"
                      class="btn btn-danger btn-sm"
                    >
                      پیام
                      <b-badge variant="dark" class="ms-1">{{
                        user.unreadMessagesCount
                      }}</b-badge>
                    </RouterLink>
                  </li>
                </ul>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="d-flex justify-content-end" v-if="users.total > users.limit">
        <div class="pagination-wrap hstack gap-2" style="display: flex">
          <ul class="pagination listjs-pagination mb-0">
            <li
              v-for="(link, i) of users.links"
              :key="i"
              class="page-item"
              :class="{
                disabled: !link.url,
                active: link.active || !link.url,
              }"
            >
              <span
                class="page-link"
                v-if="link.active"
                v-html="link.label"
              ></span>

              <a
                v-else
                href="#"
                @click="getForPage($event, link)"
                class="page-link"
                v-html="link.label"
              >
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </b-card-body>
</template>

<style>
.vpd-icon-btn {
  margin-bottom: 0 !important;
  padding: 0.5rem 0.9rem !important;
  border-top-right-radius: 0.25rem;
  border-bottom-right-radius: 0.25rem;
}
.vpd-input-group input {
  line-height: 1.5 !important;
  padding: 0.5rem 0.9rem !important;
}
</style>
